import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import '@babylonjs/core/Materials/standardMaterial';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Scene } from '@babylonjs/core/scene';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { Slider } from '@babylonjs/gui/2D/controls/sliders/slider';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { RADIO_STATIONS } from '../audio/radioStations';
import { radioStatusText } from '../audio/radioStatus';
import { soundFromSlider } from '../ui/settings';
import type { ShellStore } from '../ui/shell';
import { FAR_TABLET_LAYOUT } from './board/constants';

// Larger in metres at table distance, without changing the browser controls or GUI layout.
export const IMMERSIVE_CONTROLS_SIZE = { width: 0.54, height: (0.54 * 380) / 680 } as const;
/** The plate's canvas, and its lettering: no smaller than the headset reads from the seat (30 px). */
export const IMMERSIVE_CONTROLS_TEXTURE = { width: 680, height: 380 } as const;
export const IMMERSIVE_CONTROLS_FONTS = { vr: 36, label: 30 } as const;
/** Where the plate hangs: beside the far turn sign, on the starting player's right (world -X). */
export const IMMERSIVE_CONTROLS_CENTRE = new Vector3(
  -(FAR_TABLET_LAYOUT.width / 2 + 0.06 + IMMERSIVE_CONTROLS_SIZE.width / 2),
  FAR_TABLET_LAYOUT.y,
  FAR_TABLET_LAYOUT.z,
);

/** Fixed beside the far turn sign, on the starting player's right (world -X). */
export function anchorImmersiveControls(plane: TransformNode) {
  plane.parent = null;
  plane.position.copyFrom(IMMERSIVE_CONTROLS_CENTRE);
  plane.rotationQuaternion = Quaternion.RotationYawPitchRoll(
    Math.PI,
    Math.PI / 2 - FAR_TABLET_LAYOUT.tilt,
    0,
  );
  plane.computeWorldMatrix(true);
}

/**
 * Whether the plate takes the laser in a view: always, but in a duel only while a grip is held,
 * since there the trigger attacks and the laser may cross the plate as it does.
 */
export function plateAnswersLaser(view: string | null, pointing: boolean): boolean {
  return !view?.endsWith(':arena') || pointing;
}

/** Call after the scene has restored its rig and XR has supplied the first valid head pose. */
export function createImmersiveControlAnchor(plane: TransformNode) {
  let previous: string | null = null;
  plane.setEnabled(false);
  return (view: string | null) => {
    if (view !== null && view !== previous) anchorImmersiveControls(plane);
    plane.setEnabled(view !== null);
    previous = view;
  };
}

/** World-fixed counterpart of the browser's fixed top-right controls. Not game-owned. */
export function createImmersiveControls(
  scene: Scene,
  shell: ShellStore,
  exit: () => Promise<void>,
) {
  const plane = MeshBuilder.CreatePlane('immersiveControls', IMMERSIVE_CONTROLS_SIZE, scene);
  const anchor = createImmersiveControlAnchor(plane);
  plane.renderingGroupId = 2;
  plane.isPickable = true;
  const texture = AdvancedDynamicTexture.CreateForMesh(
    plane,
    IMMERSIVE_CONTROLS_TEXTURE.width,
    IMMERSIVE_CONTROLS_TEXTURE.height,
    false,
  );
  let updating = false;
  const place = (control: Control, x: number, y: number, w: number, h: number) => {
    control.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    control.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    control.left = x;
    control.top = y;
    control.width = `${w}px`;
    control.height = `${h}px`;
    texture.addControl(control);
  };
  const vr = Button.CreateSimpleButton('persistent-vr', 'VR');
  vr.color = '#ffe34a';
  vr.background = '#17202c';
  vr.fontSize = IMMERSIVE_CONTROLS_FONTS.vr;
  place(vr, 558, 0, 110, 76);
  vr.onPointerClickObservable.add(() => {
    void exit().catch(() => {
      status.text = 'Could not leave VR. Try again.';
    });
  });
  const radio = shell.radio;
  const settings = shell.settings;
  const stations = [...RADIO_STATIONS, { id: null, label: 'OFF' }];
  const stationButtons = stations.map((station, i) => {
    const button = Button.CreateSimpleButton(`hud-radio-${station.id ?? 'off'}`, station.label);
    button.fontSize = IMMERSIVE_CONTROLS_FONTS.label;
    button.background = '#17202c';
    place(button, (i % 3) * 180, Math.floor(i / 3) * 80, 170, 68);
    button.onPointerClickObservable.add(() => {
      void radio?.select(station.id);
    });
    return button;
  });
  const volume = (name: string, y: number, change: (value: number) => void) => {
    const label = new TextBlock(`${name}-label`, name);
    label.color = 'white';
    label.fontSize = IMMERSIVE_CONTROLS_FONTS.label;
    place(label, 0, y, 105, 58);
    const slider = new Slider(name);
    slider.minimum = 0;
    slider.maximum = 1;
    slider.color = 'white';
    slider.background = '#333';
    place(slider, 115, y + 10, 423, 35);
    slider.onValueChangedObservable.add((value) => {
      if (!updating) change(value);
    });
    return slider;
  };
  const music = volume('MUSIC', 172, (value) => radio?.setVolume(value));
  const sound = volume('SOUND', 240, (value) => settings?.set({ sound: soundFromSlider(value) }));
  const status = new TextBlock('radio-status', '');
  status.color = 'white';
  status.fontSize = IMMERSIVE_CONTROLS_FONTS.label;
  place(status, 0, 308, 668, 50);
  const refresh = () => {
    updating = true;
    const r = radio?.get(),
      s = settings?.get().sound;
    music.value = r?.volume ?? 0;
    sound.value = s?.muted ? 0 : (s?.volume ?? 0);
    music.color = s?.muted || music.value === 0 ? '#888' : 'white';
    sound.color = sound.value === 0 ? '#888' : 'white';
    stationButtons.forEach((button, i) => {
      button.color = r?.selected === stations[i]!.id ? '#ffe34a' : 'white';
    });
    status.text = r
      ? radioStatusText(r, r.selected !== null && radio!.isOutOfOrder(r.selected))
      : '';
    updating = false;
  };
  const unsubs = [
    shell.subscribe(refresh),
    radio?.subscribe(refresh),
    settings?.subscribe(refresh),
  ];
  refresh();
  return {
    /**
     * Stands the plate for a view (`null` takes it down). In a duel the trigger attacks, so the
     * plate answers the laser only while a grip is held (`pointing`): an attack pulled as the
     * laser happens to cross it neither leaves VR nor changes the station.
     */
    update(view: string | null, pointing = false) {
      anchor(view);
      plane.isPickable = plateAnswersLaser(view, pointing);
    },
    dispose() {
      unsubs.forEach((unsub) => unsub?.());
      texture.dispose();
      plane.dispose(false, true);
    },
  };
}
